import React from 'react';

// Donut chart component for score visualisation
export const DonutChart = ({ value, size = 160, strokeWidth = 12, label, sublabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  
  const getScoreColor = (score) => {
    if (score >= 70) return '#4ADE80'; // Green
    if (score >= 40) return '#FBBF24'; // Amber
    return '#FB923C'; // Orange/Coral
  };

  const color = getScoreColor(value);
  const fontSize = size < 140 ? 'text-2xl' : 'text-4xl';

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${fontSize} font-light text-[#f5f5f0]`}>{value}</span>
          {sublabel && (
            <span className="text-[10px] md:text-xs text-[#f5f5f0]/40 uppercase tracking-wider">{sublabel}</span>
          )}
        </div>
      </div>
      {label && (
        <div className="mt-2 md:mt-3 text-center">
          <span className="text-xs md:text-sm text-[#f5f5f0]/60">{label}</span>
        </div>
      )}
    </div>
  );
};

// Small donut for category scores
export const SmallDonut = ({ value, size = 48, strokeWidth = 4 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  
  const getScoreColor = (score) => {
    if (score >= 70) return '#4ADE80';
    if (score >= 40) return '#FBBF24';
    return '#FB923C';
  };

  const color = getScoreColor(value);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-medium text-[#f5f5f0]">{value}</span>
      </div>
    </div>
  );
};

// Score summary bar (horizontal) for distribution
export const ScoreSummaryBar = ({ passed, partial, failed }) => {
  const total = passed + partial + failed;
  const passedPct = total > 0 ? (passed / total) * 100 : 0;
  const partialPct = total > 0 ? (partial / total) * 100 : 0;
  const failedPct = total > 0 ? (failed / total) * 100 : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
          <span className="text-[#f5f5f0]/60">{passed} passed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-400"></div>
          <span className="text-[#f5f5f0]/60">{partial} partial</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-400"></div>
          <span className="text-[#f5f5f0]/60">{failed} failed</span>
        </div>
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden flex">
        <div 
          className="h-full bg-emerald-400 transition-all duration-1000" 
          style={{ width: `${passedPct}%` }}
        />
        <div 
          className="h-full bg-amber-400 transition-all duration-1000" 
          style={{ width: `${partialPct}%` }}
        />
        <div 
          className="h-full bg-orange-400 transition-all duration-1000" 
          style={{ width: `${failedPct}%` }}
        />
      </div>
    </div>
  );
};
